export function NewsTab({ 
  user,
  newPost,
  setNewPost,
  handlePostSubmit,
  editingPost,
  handleCancelEdit,
  postError,
  posting,
  newsStartIndex,
  postsPerPage,
  posts,
  canScrollLeft,
  canScrollRight,
  handleScrollNews,
  latestNews,
  setSelectedPost,
  handleDeletePost,
  handleEditPost,
  formatTime,
  truncateText,
  sessionVotes,
  handleVote
}) {
  return (
    <div className="section-container">
      {user && (
        <div className="section-card">
          <h3 className="section-subtitle">{editingPost ? 'Edit News' : 'Post News'}</h3>
          <form onSubmit={handlePostSubmit} className="admin-form">
            <input
              className="form-input"
              placeholder="Title"
              value={newPost.title}
              onChange={e => setNewPost({ ...newPost, title: e.target.value })}
              required
            />
            <textarea
              className="form-input"
              placeholder="Content"
              value={newPost.content}
              onChange={e => setNewPost({ ...newPost, content: e.target.value })}
              rows={3}
              required
            />
            <input
              className="form-input"
              placeholder="Image URL (optional)"
              value={newPost.image}
              onChange={e => setNewPost({ ...newPost, image: e.target.value })}
            />
            {postError && <div className="admin-error">{postError}</div>}
            <div className="flex gap-2">
              <button type="submit" className="btn-primary flex-1 py-2 text-sm" disabled={posting}>
                {posting ? (editingPost ? 'Updating...' : 'Posting...') : (editingPost ? 'Update' : 'Post')}
              </button>
              {editingPost && (
                <button 
                  type="button" 
                  onClick={handleCancelEdit}
                  className="px-6 py-2 border border-gray-600 text-gray-200 rounded-lg hover:bg-gray-700 transition-colors text-sm"
                >
                  Cancel
                </button>
              )}
            </div>
          </form>
        </div>
      )}

      <div className="section-card">
        <div className="news-header">
          <h3 className="section-subtitle">Latest News</h3>
          <div className="news-controls">
            <button
              onClick={() => handleScrollNews('left')}
              disabled={!canScrollLeft}
              className={`news-nav-button ${canScrollLeft ? 'news-nav-active' : 'news-nav-disabled'}`}
              aria-label="Previous posts"
            >
              ←
            </button>
            <span className="news-counter">
              {newsStartIndex + 1}-{Math.min(newsStartIndex + postsPerPage, posts.length)} of {posts.length}
            </span>
            <button
              onClick={() => handleScrollNews('right')}
              disabled={!canScrollRight}
              className={`news-nav-button ${canScrollRight ? 'news-nav-active' : 'news-nav-disabled'}`}
              aria-label="Next posts"
            >
              →
            </button>
          </div>
        </div>
        <div className="news-grid">
          {latestNews.map(post => (
            <div 
              key={post.id} 
              className="news-card"
              onClick={() => setSelectedPost(post)}
            >
              {user && (
                <div className="flex gap-2 absolute top-2 right-2 z-10">
                  <button
                    onClick={(e) => {
                      e.stopPropagation();
                      handleEditPost(post);
                    }}
                    className="bg-blue-600 hover:bg-blue-700 text-white p-1.5 rounded-full shadow-lg transition-colors"
                    title="Edit post"
                    aria-label="Edit post"
                  >
                    <svg xmlns="http://www.w3.org/2000/svg" className="h-3.5 w-3.5" viewBox="0 0 20 20" fill="currentColor">
                      <path d="M13.586 3.586a2 2 0 112.828 2.828l-.793.793-2.828-2.828.793-.793zM11.379 5.793L3 14.172V17h2.828l8.38-8.379-2.83-2.828z" />
                    </svg>
                  </button>
                  <button
                    onClick={(e) => {
                      e.stopPropagation();
                      handleDeletePost(post.id);
                    }}
                    className="bg-red-600 hover:bg-red-700 text-white p-1.5 rounded-full shadow-lg transition-colors"
                    title="Delete post"
                    aria-label="Delete post"
                  >
                    <svg xmlns="http://www.w3.org/2000/svg" className="h-3.5 w-3.5" viewBox="0 0 20 20" fill="currentColor">
                      <path fillRule="evenodd" d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z" clipRule="evenodd" />
                    </svg>
                  </button>
                </div>
              )}
              <div className="news-timestamp">{formatTime(post.time)}</div>
              {post.image && (
                <img
                  src={post.image}
                  alt={post.title}
                  className="news-image"
                  onError={(e) => {
                    e.currentTarget.style.display = 'none';
                  }}
                />
              )}
              <h4 className="news-title">{post.title}</h4>
              <p className="news-content">{truncateText(post.content, 80)}</p>
              <div className="flex gap-2 items-center" onClick={(e) => e.stopPropagation()}>
                <button
                  className={`btn-vote text-sm flex items-center ${sessionVotes[post.id] === 'agree' ? 'btn-vote-active-agree' : ''}`}
                  onClick={() => handleVote(post.id, 'agree')}
                  title="Agree"
                >
                  <span className="mr-1" role="img" aria-label="thumbs up">👍</span>
                  {post.agree || 0}
                </button>
                <button
                  className={`btn-vote text-sm flex items-center ${sessionVotes[post.id] === 'disagree' ? 'btn-vote-active-disagree' : ''}`}
                  onClick={() => handleVote(post.id, 'disagree')}
                  title="Disagree"
                >
                  <span className="mr-1" role="img" aria-label="thumbs down">👎</span>
                  {post.disagree || 0}
                </button>
              </div>
              <div className="news-read-more">Click to read more →</div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}
